/**
 * Token Update Utility
 * Allows services to update token balance optimistically without direct React hook access
 */

let updateTokenBalanceOptimistically: ((tokensCharged: number) => void) | null = null;
let syncTokenBalanceFromBackend: ((tokensRemaining: number, tokensUsed: number) => void) | null = null;

/**
 * Set the token balance update function (called from SubscriptionContext)
 */
export function setTokenBalanceUpdater(
  updater: (tokensCharged: number) => void
): void {
  updateTokenBalanceOptimistically = updater;
}

/**
 * Set the token balance sync function (called from SubscriptionContext)
 */
export function setTokenBalanceSyncer(
  syncer: (tokensRemaining: number, tokensUsed: number) => void
): void {
  syncTokenBalanceFromBackend = syncer;
}

/**
 * Update token balance optimistically (called from services)
 * @param tokensCharged Number of tokens charged for the operation
 */
export function updateTokenBalance(tokensCharged: number): void {
  if (updateTokenBalanceOptimistically) {
    updateTokenBalanceOptimistically(tokensCharged);
  } else {
    // Silently fail if updater is not set (e.g., during SSR or before context is initialized)
    console.debug('Token balance updater not available');
  }
}

/**
 * Sync token balance from backend response (called from services after receiving response)
 * @param tokensRemaining Updated token balance from backend
 * @param tokensUsed Updated tokens used from backend
 */
export function syncTokenBalance(tokensRemaining: number, tokensUsed: number): void {
  if (syncTokenBalanceFromBackend) {
    syncTokenBalanceFromBackend(tokensRemaining, tokensUsed);
  } else {
    // Silently fail if syncer is not set (e.g., during SSR or before context is initialized)
    console.debug('Token balance syncer not available');
  }
}

/**
 * Token costs are no longer hardcoded in the frontend.
 * The backend is the single source of truth for token costs.
 * Frontend should use tokensRemaining and tokensUsed from backend responses.
 * 
 * @deprecated TOKEN_COSTS removed - use backend response tokensRemaining/tokensUsed instead
 */

